import Splide from '@splidejs/splide';

document.addEventListener('DOMContentLoaded', () => {
  const siteHeader = document.querySelector('.site-header');
  const navToggle = document.querySelector('.site-header__toggle');

  if (siteHeader) {
    let isTicking = false;

    const updateHeaderState = () => {
      siteHeader.classList.toggle('is-scrolled', window.scrollY > 24);
      isTicking = false;
    };

    updateHeaderState();

    window.addEventListener('scroll', () => {
      if (!isTicking) {
        window.requestAnimationFrame(updateHeaderState);
        isTicking = true;
      }
    }, { passive: true });
  }

  if (siteHeader && navToggle) {
    navToggle.addEventListener('click', () => {
      const isOpen = siteHeader.classList.toggle('is-nav-open');

      navToggle.setAttribute('aria-expanded', String(isOpen));
    });

    siteHeader.querySelectorAll('.site-header__nav a').forEach((link) => {
      link.addEventListener('click', () => {
        siteHeader.classList.remove('is-nav-open');
        navToggle.setAttribute('aria-expanded', 'false');
      });
    });
  }

  const popupConfig = {
    enabled: true,
    rememberDismissal: true, // Set to false while testing to show the popup on every page load.
    dismissalDays: 7,
    showDelay: 3000,
    storageKey: 'olympiaConsultationPopupDismissedUntil',
  };

  const getStoredDismissal = () => {
    try {
      return Number(window.localStorage.getItem(popupConfig.storageKey) || 0);
    } catch (error) {
      return 0;
    }
  };

  const setStoredDismissal = () => {
    if (!popupConfig.rememberDismissal) {
      return;
    }

    const dismissedUntil = Date.now() + (popupConfig.dismissalDays * 24 * 60 * 60 * 1000);

    try {
      window.localStorage.setItem(popupConfig.storageKey, String(dismissedUntil));
    } catch (error) {
      // localStorage can be unavailable in private or restricted browsing modes.
    }
  };

  document.querySelectorAll('[data-dismissible-popup]').forEach((popup) => {
    const closeButton = popup.querySelector('[data-dismiss-popup]');
    const isDismissed = popupConfig.rememberDismissal && getStoredDismissal() > Date.now();

    if (!popupConfig.enabled || isDismissed) {
      popup.classList.add('is-dismissed');
      return;
    }

    window.setTimeout(() => {
      if (!popup.classList.contains('is-dismissed')) {
        popup.classList.add('is-visible');
      }
    }, popupConfig.showDelay);

    closeButton?.addEventListener('click', () => {
      if (!popup.classList.contains('is-dismissing')) {
        setStoredDismissal();
        popup.classList.add('is-dismissing');

        let dismissTimer;
        const dismissPopup = () => {
          window.clearTimeout(dismissTimer);
          popup.removeEventListener('animationend', handleAnimationEnd);
          popup.classList.add('is-dismissed');
          popup.classList.remove('is-dismissing', 'is-visible');
        };

        const handleAnimationEnd = (event) => {
          if (event.animationName === 'consultationFloatOut') {
            dismissPopup();
          }
        };

        popup.addEventListener('animationend', handleAnimationEnd);
        dismissTimer = window.setTimeout(dismissPopup, 500);
      }
    });
  });

  document.querySelectorAll('[data-service-slider]').forEach((slider) => {
    const section = slider.closest('.home-service-slider');
    const previousButton = section?.querySelector('[data-service-slider-prev]');
    const nextButton = section?.querySelector('[data-service-slider-next]');
    const dotsContainer = section?.querySelector('[data-service-slider-dots]');
    const slides = slider.querySelectorAll('.splide__slide');

    if (!section || !dotsContainer || slides.length < 2) {
      return;
    }

    const splide = new Splide(slider, {
      type: 'loop',
      perPage: 4,
      perMove: 1,
      gap: 'var(--service-slider-gap)',
      arrows: false,
      pagination: false,
      autoplay: true,
      interval: 4200,
      speed: 650,
      pauseOnHover: true,
      pauseOnFocus: true,
      resetProgress: true,
      waitForTransition: true,
      clones: slides.length,
      breakpoints: {
        '1199.98': {
          perPage: 3,
        },
        '767.98': {
          perPage: 2,
        },
        '575.98': {
          perPage: 2,
        },
      },
    });

    const updateDots = () => {
      const activeIndex = ((splide.index % slides.length) + slides.length) % slides.length;

      dotsContainer.querySelectorAll('.home-service-slider__dot').forEach((dot, index) => {
        const isActive = index === activeIndex;

        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };

    const syncCardHeight = () => {
      slider.style.removeProperty('--service-card-height');

      requestAnimationFrame(() => {
        const maxHeight = Math.max(
          ...Array.from(slider.querySelectorAll('.splide__slide:not(.splide__slide--clone)')).map((slide) => (
            slide.getBoundingClientRect().height
          ))
        );

        if (maxHeight > 0) {
          slider.style.setProperty('--service-card-height', `${Math.ceil(maxHeight)}px`);
        }
      });
    };

    const restartAutoplay = () => {
      const { Autoplay } = splide.Components;

      if (Autoplay) {
        Autoplay.pause();
        Autoplay.play();
      }
    };

    const buildDots = () => {
      dotsContainer.innerHTML = '';

      slides.forEach((_, index) => {
        const dot = document.createElement('button');

        dot.className = 'home-service-slider__dot';
        dot.type = 'button';
        dot.setAttribute('aria-label', `Go to service ${index + 1}`);
        dot.addEventListener('click', () => {
          splide.go(index);
          restartAutoplay();
        });

        dotsContainer.appendChild(dot);
      });
    };

    buildDots();

    splide.on('mounted moved updated resized', updateDots);
    splide.on('mounted updated resized', syncCardHeight);
    splide.mount();

    slider.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', syncCardHeight, { once: true });
      }
    });

    nextButton?.addEventListener('click', () => {
      splide.go('+1');
      restartAutoplay();
    });

    previousButton?.addEventListener('click', () => {
      splide.go('-1');
      restartAutoplay();
    });
  });
});
